import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import HealthPhotoUpload from '../components/health/HealthPhotoUpload'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { healthService } from '../services/healthService'

const Health = () => {
  const { user } = useAuth()
  const [healthRecords, setHealthRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('upload')
  
  // Health chat state
  const [chatQuestion, setChatQuestion] = useState('')
  const [chatAnswer, setChatAnswer] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [chatHistory, setChatHistory] = useState([])
  const [hasAskedQuestion, setHasAskedQuestion] = useState(false)

  useEffect(() => {
    loadHealthRecords()
  }, [])

  const loadHealthRecords = async () => {
    try {
      setLoading(true)
      const response = await healthService.getHealthRecords()
      console.log('Health records response:', response)
      
      // Since api.js returns response.data, the response should already be unwrapped
      setHealthRecords(response.data || response || [])
    } catch (error) {
      toast.error('Failed to load health records')
      console.error('Health records error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUploadSuccess = (newRecord) => {
    setHealthRecords(prev => [newRecord, ...prev])
    toast.success('Health photo uploaded successfully!')
    setActiveTab('records')
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'processing':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleHealthChat = async (e) => {
    e.preventDefault()
    
    if (!chatQuestion.trim()) {
      toast.error('Please enter a question about your health')
      return
    }

    setChatLoading(true)
    setHasAskedQuestion(true)

    try {
      const response = await healthService.askHealthQuestion(chatQuestion.trim())
      
      const newChatEntry = {
        question: chatQuestion.trim(),
        answer: response.data.answer,
        timestamp: new Date(),
        recordsAnalyzed: response.data.recordsAnalyzed,
        hasHistory: response.data.hasHistory
      }
      
      setChatHistory(prev => [newChatEntry, ...prev])
      setChatAnswer(response.data.answer)
      setChatQuestion('')
      
      toast.success(`Analyzed ${response.data.recordsAnalyzed} health records to answer your question`)
    } catch (error) {
      console.error('Health chat error:', error)
      toast.error('Failed to get health insights')
      setChatAnswer('Sorry, I encountered an error while analyzing your health history. Please try again.')
    } finally {
      setChatLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Health Analysis 🏥
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Upload photos for AI-powered health insights and track your analysis history
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Total Analyses: {healthRecords.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('upload')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'upload'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Upload Photo
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'records'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Analysis History ({healthRecords.length})
            </button>
            <button
              onClick={() => setActiveTab('chat')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'chat'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              🤖 Health Insights Chat
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'upload' && (
            <div className="max-w-2xl">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Upload Health Photo for AI Analysis
                </h2>
                <p className="text-gray-600 mb-6">
                  Upload a photo related to your health concern for AI-powered analysis. 
                  Our system will provide insights, identify potential concerns, and suggest recommendations.
                </p>
                <HealthPhotoUpload onUploadSuccess={handleUploadSuccess} />
              </div>

              {/* Information Card */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.937c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.937zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Important Notice</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        This AI analysis is for informational purposes only and should not replace 
                        professional medical advice, diagnosis, or treatment. Always consult with 
                        qualified healthcare professionals for medical concerns.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'records' && (
            <div>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="large" />
                </div>
              ) : healthRecords.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏥</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No health records yet
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Upload your first health photo to get AI-powered analysis
                  </p>
                  <button
                    onClick={() => setActiveTab('upload')}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Upload Photo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {healthRecords.map((record) => (
                    <div key={record._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      {/* Image */}
                      <div className="aspect-w-16 aspect-h-12 bg-gray-200">
                        <img
                          src={`http://localhost:5000${record.imageUrl}`}
                          alt={record.title}
                          className="w-full h-48 object-cover"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzVMMTI1IDEwMEgxMTJWMTI1SDg4VjEwMEg3NUwxMDAgNzVaIiBmaWxsPSIjOUI5QkEwIi8+CjwvZz4KPC9zdmc+'
                          }}
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {record.title}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                            {record.status}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3">
                          {record.description || 'No description provided'}
                        </p>
                        
                        {record.analysis && (
                          <div className="mb-3 space-y-3">
                            {/* Summary */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-1">Summary</h4>
                              <p className="text-sm text-gray-800">
                                {record.analysis.summary}
                              </p>
                            </div>

                            {/* Detailed Analysis */}
                            {record.analysis.details && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Detailed Analysis</h4>
                                <p className="text-sm text-gray-700">
                                  {record.analysis.details}
                                </p>
                              </div>
                            )}

                            {/* Concerns */}
                            {record.analysis.concerns && record.analysis.concerns.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Health Concerns</h4>
                                <div className="space-y-1">
                                  {record.analysis.concerns.map((concern, index) => (
                                    <div key={index} className={`flex items-center space-x-2 px-2 py-1 rounded text-xs ${
                                      concern.level === 'high' ? 'bg-red-100 text-red-800' :
                                      concern.level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-blue-100 text-blue-800'
                                    }`}>
                                      <span className="font-medium capitalize">{concern.level}:</span>
                                      <span>{concern.description}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Recommendations */}
                            {record.analysis.recommendations && record.analysis.recommendations.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Recommendations</h4>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {record.analysis.recommendations.map((recommendation, index) => (
                                    <li key={index} className="flex items-start space-x-1">
                                      <span className="text-primary-600 mt-1">•</span>
                                      <span>{recommendation}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Confidence */}
                            {record.analysis.confidence && (
                              <div>
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>Analysis Confidence</span>
                                  <span>{record.analysis.confidence}%</span>
                                </div>
                                <div className="bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className={`h-1.5 rounded-full ${
                                      record.analysis.confidence >= 80 ? 'bg-green-600' :
                                      record.analysis.confidence >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                                    }`}
                                    style={{ width: `${record.analysis.confidence}%` }}
                                  ></div>
                                </div>
                              </div>
                            )}

                            {/* Historical Insights */}
                            {record.analysis.patterns && record.analysis.patterns.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Patterns Identified</h4>
                                <div className="space-y-1">
                                  {record.analysis.patterns.map((pattern, index) => (
                                    <div key={index} className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded">
                                      🔍 {pattern}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Progress Assessment */}
                            {record.analysis.progressAssessment && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-1">Progress Assessment</h4>
                                <p className="text-sm text-indigo-700 bg-indigo-50 p-2 rounded">
                                  📈 {record.analysis.progressAssessment}
                                </p>
                              </div>
                            )}

                            {/* Historical Context Indicator */}
                            {record.metadata?.hasHistoricalContext && (
                              <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full inline-flex items-center">
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                                Analyzed with {record.metadata.historicalRecordsAnalyzed} previous records
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          {formatDate(record.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="max-w-4xl">
              {/* Health Chat Header */}
              <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200 p-6 mb-6">
                <div className="flex items-center space-x-2 mb-4">
                  <span className="text-2xl">🤖</span>
                  <h2 className="text-lg font-semibold text-gray-900">Health Insights Chat</h2>
                </div>
                <p className="text-gray-600">
                  Ask questions about your health history and get personalized insights based on all your uploaded health records. 
                  I can track patterns, progress, and provide tailored recommendations.
                </p>
                <div className="mt-4 text-sm text-blue-700 bg-blue-100 rounded-lg p-3">
                  💡 <strong>Try asking:</strong> "How has my condition progressed over time?" or "What patterns do you see in my health records?"
                </div>
              </div>

              {/* Chat Interface */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <form onSubmit={handleHealthChat} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ask about your health history
                    </label>
                    <textarea
                      value={chatQuestion}
                      onChange={(e) => setChatQuestion(e.target.value)}
                      placeholder="What would you like to know about your health history? (e.g., 'What trends do you notice?', 'How has my condition changed?', 'What should I monitor?')"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      disabled={chatLoading}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      {healthRecords.length > 0 ? (
                        `Ready to analyze ${healthRecords.length} health records`
                      ) : (
                        'Upload some health photos first to get personalized insights'
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={chatLoading || !chatQuestion.trim() || healthRecords.length === 0}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center space-x-2"
                    >
                      {chatLoading && <LoadingSpinner size="small" />}
                      <span>{chatLoading ? 'Analyzing...' : 'Get Insights'}</span>
                    </button>
                  </div>
                </form>

                {/* Chat History */}
                {hasAskedQuestion && (
                  <div className="mt-6 space-y-4">
                    <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                      Health Insights
                    </h3>
                    
                    {chatHistory.map((entry, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <span className="text-blue-600 mt-1">❓</span>
                            <div>
                              <p className="font-medium text-blue-900">Your Question:</p>
                              <p className="text-blue-800">{entry.question}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="flex items-start space-x-2">
                            <span className="text-green-600 mt-1">🤖</span>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <p className="font-medium text-green-900">AI Health Insights:</p>
                                <div className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded-full">
                                  Analyzed {entry.recordsAnalyzed} records
                                </div>
                              </div>
                              <div className="text-green-800 whitespace-pre-wrap">{entry.answer}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500 text-right">
                          {formatDate(entry.timestamp)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No History Message */}
                {healthRecords.length === 0 && (
                  <div className="mt-6 text-center py-8 bg-gray-50 rounded-lg">
                    <div className="text-4xl mb-2">📊</div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Health History Available
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Upload some health photos first to start getting personalized insights about your health journey.
                    </p>
                    <button
                      onClick={() => setActiveTab('upload')}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                    >
                      Upload First Health Photo
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Health
